import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { readStored, writeStored } from '@/components/desktop';
import { CheckMenu } from '@/components/desktop/CheckMenu';
import { useAuth } from '@/stores/auth';
import { useConnection } from '@/stores/connection';
import { figuresUpdatedAt } from '@/stores/queryCache';
import { useOutboxStatus } from '@/offline/sender';
import { useOfflineCopy } from '@/offline/session';
import { useShellUi } from '@/stores/shellUi';
import { formatSince, formatTime } from '@/format';
import { useAppUpdate } from './serviceWorker';
import { farmScopeLabel, useFarmScope } from '@/stores/farmScope';
import { AccountMenu } from './AccountMenu';
import { HeaderClock } from './HeaderClock';
import { MODULES, canSee, isComingSoon, screenTag, type ModuleDef } from './modules';
import { ModuleIcon, SettingsIcon } from './ModuleIcon';

const RAIL_KEY = 'poultry.shell.railOpen';

/**
 * The application shell: dark rail, top bar, and the active screen.
 *
 * Navigation is built from the user's permissions rather than their role name,
 * so a custom role created in the admin screens gets a correct sidebar with no
 * code change.
 */
export function Shell({
  active,
  onNavigate,
  children,
}: {
  active: string;
  onNavigate: (screenId: string) => void;
  children: ReactNode;
}) {
  const { user, can } = useAuth();
  const online = useConnection((s) => s.online);
  const offlineSince = useConnection((s) => s.offlineSince);
  const updateReady = useAppUpdate((s) => s.ready);
  const applyUpdate = useAppUpdate((s) => s.apply);
  const queryClient = useQueryClient();
  const openDialog = useShellUi((s) => s.open);
  // Another screen starts at its top: a long form scrolled down left the next
  // screen opened from it — Offline work, from the "Saved on this PC" dialog —
  // with its tabs scrolled out of sight.
  const canvas = useRef<HTMLElement>(null);
  useEffect(() => {
    canvas.current?.scrollTo?.(0, 0);
  }, [active]);
  // Remembered like every other pane: someone who likes a narrow sidebar
  // should not have to close it again after every reload.
  const [railOpen, setRailOpenState] = useState(() => readStored(RAIL_KEY, true));
  const setRailOpen = (update: (open: boolean) => boolean) =>
    setRailOpenState((open) => {
      const next = update(open);
      writeStored(RAIL_KEY, next);
      return next;
    });

  if (!user) return null;

  const visible = MODULES.filter((m) => m.screens.some((s) => canSee(s, can)));
  const activeModule = visible.find((m) => m.screens.some((s) => s.id === active));
  const subScreens = activeModule?.screens.filter((s) => canSee(s, can)) ?? [];

  // "HR & Payroll › Employees" — where you are, in the same words as the
  // sidebar. The module is left off when it would only repeat the screen.
  const screenName =
    active === 'settings'
      ? 'Settings'
      : (subScreens.find((s) => s.id === active)?.label ?? 'Dashboard');
  const moduleName =
    active !== 'settings' && activeModule && activeModule.label !== screenName
      ? activeModule.label
      : null;

  return (
    <div className="shell">
      <nav className={`rail ${railOpen ? '' : 'rail--closed'}`} aria-label="Modules">
        <div className="rail__head">
          {railOpen && (
            <div className="rail__brand">
              <div className="rail__wordmark">Poultry MS</div>
            </div>
          )}
          <button
            className="rail__toggle"
            onClick={() => setRailOpen((v) => !v)}
            aria-label={railOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            aria-expanded={railOpen}
          >
            {railOpen ? '«' : '»'}
          </button>
        </div>

        <ul className="rail__list">
          {visible.map((m) => (
            <RailItem
              key={m.id}
              module={m}
              open={railOpen}
              active={activeModule?.id === m.id}
              onSelect={() => {
                const first = m.screens.find((s) => canSee(s, can));
                if (first) onNavigate(first.id);
              }}
            />
          ))}
        </ul>

        <div className="rail__settings">
          <button
            className={`rail__item ${active === 'settings' ? 'rail__item--on' : ''}`}
            onClick={() => onNavigate('settings')}
            title={railOpen ? undefined : 'Settings'}
            aria-current={active === 'settings' ? 'page' : undefined}
          >
            <SettingsIcon active={active === 'settings'} />
            {railOpen && <span className="rail__label">Settings</span>}
          </button>
        </div>
      </nav>

      <div className="main">
        <header className="topbar" aria-label="Top bar">
          <h1 className="topbar__crumb">
            {moduleName && <span className="topbar__crumbmodule">{moduleName} › </span>}
            <span className="topbar__crumbscreen">{screenName}</span>
          </h1>

          {/* One way to search, not two: this opens the same Ctrl+K window,
              which finds screens, Settings pages and commands alike. */}
          <button
            type="button"
            className="topbar__search"
            onClick={() => openDialog('palette')}
            aria-label="Search screens and commands"
          >
            <span className="topbar__searchicon" aria-hidden="true">
              ⌕
            </span>
            <span className="topbar__searchtext">Search screens and commands</span>
            <kbd className="topbar__searchkey">Ctrl+K</kbd>
          </button>

          {/* Everything about this window, kept together at one end rather
              than spread across the bar by the space between them. */}
          <div className="topbar__tools">
            <OutboxLight online={online} offlineSince={offlineSince} onOpen={() => onNavigate('home-stuck')} />
            <FarmPicker />
            <HeaderClock onCalendar={active === 'home-calendar'} />
            <button
              className="topbar__icon"
              onClick={() => openDialog('help')}
              title="Help (F1)"
              aria-label="Help"
            >
              ?
            </button>
            <AccountMenu onNavigate={onNavigate} />
          </div>
        </header>

        {subScreens.length > 1 && activeModule?.id !== 'home' && (
          <nav className="subnav" aria-label="Screens">
            {subScreens.map((s) => (
              <button
                key={s.id}
                onClick={() => onNavigate(s.id)}
                className={`subnav__item ${s.id === active ? 'subnav__item--on' : ''}`}
                aria-current={s.id === active ? 'page' : undefined}
              >
                {s.icon && <AppIcon kind={s.icon} size={15} />}
                {s.label}
                {/* One tag at most — screenTag decides which, so a tab can never
                    be labelled as both arriving and leaving. */}
                {(() => {
                  const tag = screenTag(s);
                  if (!tag) return null;
                  return (
                    <span
                      className={`subnav__tag subnav__tag--${tag.kind === 'removing' ? 'soon' : 'coming'}`}
                      {...(tag.kind === 'removing' ? { title: tag.title } : {})}
                    >
                      {tag.label}
                    </span>
                  );
                })()}
              </button>
            ))}
          </nav>
        )}

        {!online && <OfflineBar since={offlineSince} lastSeen={figuresUpdatedAt(queryClient)} />}

        {updateReady && (
          <div className="updatebar" role="status">
            <span>A new version is ready.</span>
            <button type="button" className="btn btn--sm" onClick={applyUpdate}>
              Reload to use it
            </button>
          </div>
        )}

        <main className="canvas" ref={canvas}>
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * Where the work saved on this PC stands, always in the same place (.docs/023):
 * offline or not, how much is waiting to be sent, and whether the server
 * refused anything. A refusal opens Needs attention, because nothing else in
 * its lane can go until somebody decides.
 */
function OutboxLight({
  online,
  offlineSince,
  onOpen,
}: {
  online: boolean;
  offlineSince: number | null;
  onOpen: () => void;
}) {
  const { waiting, refused, held, sending } = useOutboxStatus();
  const copyOpen = useOfflineCopy((s) => s.open);

  const words =
    held === 'too-old'
      ? 'Reload to send your work'
      : held === 'sign-in'
        ? 'Sign in again to send your work'
        : refused > 0
          ? `${refused} need${refused === 1 ? 's' : ''} attention`
          : waiting > 0
            ? sending
              ? `Sending ${waiting}…`
              : `${waiting} waiting to send`
            : copyOpen
              ? // Still a way into Offline work, and what went from there, with nothing waiting.
                online
                ? 'All sent'
                : 'Nothing waiting'
              : null;

  return (
    <>
      {!online && (
        <span className="topbar__offline" role="status">
          <span aria-hidden="true">⚠ </span>Offline{offlineSince ? ` since ${formatSince(offlineSince)}` : ''}
        </span>
      )}
      {words && (
        <button
          type="button"
          className={`topbar__outbox${refused > 0 || held ? ' topbar__outbox--alert' : ''}`}
          onClick={onOpen}
          title="Offline work: what is saved on this PC"
        >
          {words}
        </button>
      )}
    </>
  );
}

/**
 * What is on screen while the server cannot be reached.
 *
 * It names the time the figures were loaded, because the difference between
 * "this is now" and "this was an hour ago" is the whole point — someone
 * ordering feed against a stale number is worse off than someone who knows
 * they have no number at all.
 */
function OfflineBar({ since, lastSeen }: { since: number | null; lastSeen: Date | null }) {
  return (
    <div className="offlinebar" role="alert">
      <span aria-hidden="true">⚠ </span>
      <strong>Offline mode{since ? ` since ${formatSince(since)}` : ''}.</strong> No connection.{' '}
      {lastSeen
        ? `These figures were loaded at ${formatTime(lastSeen)}.`
        : 'Nothing has been loaded yet.'}{' '}
      {/* Stock work goes through the outbox (.docs/023); other modules do not
          yet, so the bar says which is which rather than promising either. */}
      Stock work, and new items, units, categories and suppliers, are kept on this PC and sent by themselves when the connection is back. Other work needs the connection.
    </div>
  );
}

/**
 * Narrows every screen to one or several farms together.
 *
 * Only farms the user is permitted to see are offered, and the server applies
 * the same scope again, so this can only ever narrow the view. Choosing a
 * farm here replaces whatever any screen's own filter had narrowed it to —
 * see `farmScope.ts`.
 */
function FarmPicker() {
  const { selectedFarmIds, select } = useFarmScope();
  const { can } = useAuth();

  const { data: farms } = useQuery({
    queryKey: ['farms'],
    queryFn: () => api.get('/api/v1/farms'),
    enabled: can('mdm.farm.read'),
  });

  // Remembered farms that have since been deleted, or that this user can no
  // longer see, must not silently filter everything out.
  useEffect(() => {
    if (!farms || selectedFarmIds.length === 0) return;
    const stillVisible = selectedFarmIds.filter((id) => farms.some((f) => f.id === id));
    if (stillVisible.length !== selectedFarmIds.length) select(stillVisible);
  }, [farms, selectedFarmIds, select]);

  if (!farms || farms.length === 0) return null;

  function toggle(farmId: string) {
    select(
      selectedFarmIds.includes(farmId)
        ? selectedFarmIds.filter((id) => id !== farmId)
        : [...selectedFarmIds, farmId],
    );
  }

  return (
    <CheckMenu
      wrapClassName="farmpicker"
      menuClassName="farmpicker__menu"
      menuLabel="Choose farms to view"
      trigger={(open, toggleOpen) => (
        <button
          type="button"
          className="farmpicker__trigger"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={toggleOpen}
          aria-label="Filter by farm"
        >
          <span className="mono">Farm</span>
          <span>{farmScopeLabel(farms, selectedFarmIds)}</span>
        </button>
      )}
    >
      <label className="farmpicker__item">
        <input
          type="checkbox"
          checked={selectedFarmIds.length === 0}
          onChange={() => select([])}
        />
        <span>All farms ({farms.length})</span>
      </label>
      {farms.map((f) => (
        <label key={f.id} className="farmpicker__item">
          <input
            type="checkbox"
            checked={selectedFarmIds.includes(f.id)}
            onChange={() => toggle(f.id)}
          />
          <span>
            {f.code} — {f.nameEn}
          </span>
        </label>
      ))}
    </CheckMenu>
  );
}

function RailItem({
  module,
  open,
  active,
  onSelect,
}: {
  module: ModuleDef;
  open: boolean;
  active: boolean;
  onSelect: () => void;
}) {
  const soon = isComingSoon(module);
  return (
    <li>
      <button
        className={`rail__item ${active ? 'rail__item--on' : ''}`}
        onClick={onSelect}
        title={open ? undefined : soon ? `${module.label} (coming soon)` : module.label}
        aria-current={active ? 'page' : undefined}
      >
        <ModuleIcon id={module.id} active={active} />
        {open && <span className="rail__label">{module.label}</span>}
        {open && soon && <span className="rail__tag">Coming soon</span>}
      </button>
    </li>
  );
}
